import { useSnapshot } from "@iliotech/data-wire";
import { ComparatorWrapper } from "../ComparatorWrapper";
import s from "../shared.module.scss";
import { SimpleTable } from "src/main";
import { GridColumnProps } from "@progress/kendo-react-grid";
import { CellWrapper } from "@iliotech/widgets/src/insights/MarketLevel/MarketInsightDiscoveryTable/cells";
import moment from "moment";
import { ReactElement } from "react";

interface IProps {
  valueFormatter: (v: number) => string;
  virtualId: string;
  basePortfolioId: string;
}

const percentFormatter = (v: number, dp?: number) => `${v.toFixed(dp || 0)}%`;

export const CombinedColumns: (
  valueFormatter: (v: number, dp?: number, hideCurrency?: boolean) => string
) => GridColumnProps[] = (
  valueFormatter: (v: number, dp?: number, hideCurrency?: boolean) => string
) => [
  {
    field: "name",
    title: "Name",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total" ? <b>Total</b> : props.dataItem?.name}
      </td>
    ),
  },
  {
    field: "costPrice",
    title: "Price",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total"
          ? ""
          : valueFormatter(props.dataItem?.costPrice || 0, 2)}
      </td>
    ),
  },
  {
    field: "oldQuantity",
    title: "Old Quantity",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total"
          ? ""
          : valueFormatter(props.dataItem?.oldQuantity || 0, 2, true)}
      </td>
    ),
  },
  {
    field: "oldPercentOfPortfolio",
    title: "Old Portfolio %",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total"
          ? ""
          : percentFormatter(props.dataItem?.oldPercentOfPortfolio || 0, 1)}
      </td>
    ),
  },
  {
    field: "oldValue",
    title: "Old Value",
    removeBorderLeft: true,
    cell: (props) => (
      <td>{valueFormatter(props.dataItem?.oldTotalValue || 0)}</td>
    ),
  },

  {
    field: "percentOfPortfolio",
    title: "New Portfolio %",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total"
          ? ""
          : percentFormatter(props.dataItem?.percentOfPortfolio || 0, 1)}
      </td>
    ),
  },
  {
    field: "quantity",
    title: "New Quantity",
    removeBorderLeft: true,
    cell: (props) => (
      <td>
        {props.dataItem?.name === "Total"
          ? ""
          : valueFormatter(props.dataItem?.quantity || 0, 2, true)}
      </td>
    ),
  },

  {
    field: "newValue",
    title: "New Value",
    removeBorderLeft: true,
    cell: (props) => (
      <td>{valueFormatter(props.dataItem?.totalCostBase || 0)}</td>
    ),
  },
];

export const PositionComparator = ({
  valueFormatter,
  basePortfolioId,
  virtualId,
}: IProps) => {
  const { data: baseSnapshot, isLoading: baseLoading } = useSnapshot(
    basePortfolioId!,
    {
      to: moment(moment(new Date())).subtract(1, "year").add(1, "day").toDate(),
    }
  );
  const { data: virtualSnapshot, isLoading: virtualLoading } = useSnapshot(
    virtualId!,
    {
      to: moment(moment(new Date())).subtract(1, "year").add(1, "day").toDate(),
    }
  );

  const baseTotalValue = baseSnapshot?.data?.reduce(
    (acc: number, item: any) =>
      acc + (item.totalCostBase - (item.incomeHistoricBase || 0)),
    0
  );

  const virtualTotalValue = virtualSnapshot?.data?.reduce(
    (acc: number, item: any) =>
      acc + (item.totalCostBase - (item.incomeHistoricBase || 0)),
    0
  );

  // Create a map of base snapshot items by name
  const baseSnapshotMap = new Map(
    (baseSnapshot?.data || []).map((item) => [
      item.name,
      {
        ...item,
        quantity:
          item.riskAssetClassCode == "CashAndEquivalents"
            ? item.quantity - (item.incomeHistoricBase || 0)
            : item.quantity,
        percentOfPortfolio: (item.totalCostBase / baseTotalValue!) * 100,
      },
    ])
  );

  // Combine the data
  const combinedData = [
    ...(virtualSnapshot?.data || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => {
        const baseItem = baseSnapshotMap.get(item.name);
        return {
          ...item,
          quantity:
            item.riskAssetClassCode == "CashAndEquivalents"
              ? item.quantity - (item.incomeHistoricBase || 0)
              : item.quantity,
          percentOfPortfolio: (item.totalCostBase / virtualTotalValue!) * 100,
          oldQuantity: baseItem?.quantity || 0,
          oldCostPrice: baseItem?.costPrice || 0,
          oldPercentOfPortfolio: baseItem?.percentOfPortfolio || 0,
          oldTotalValue: baseItem
            ? baseItem.totalCostBase - (baseItem.incomeHistoricBase || 0)
            : 0,
        };
      }),
    {
      name: "Total",
      totalCostBase: virtualTotalValue,
      oldTotalValue: baseTotalValue,
      quantity: 0,
      costPrice: 0,
    },
  ];

  return (
    <ComparatorWrapper
      loading={baseLoading || virtualLoading}
      noData={!baseSnapshot?.data || !virtualSnapshot?.data}
    >
      <h3>Positions</h3>
      <article className={s.card}>
        <SimpleTable
          data={combinedData}
          columns={CombinedColumns(valueFormatter)}
        />
      </article>
    </ComparatorWrapper>
  );
};
